import { useCallback, useEffect, useState } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config";
import {
  extractInvitationList,
  normaliseInvitationRow,
  normaliseRow,
} from "../components/supplierManagementHelpers";
import { fetchSupplierIndustries } from "./supplierIndustries";

/**
 * Owns supplier-management page data: registered-suppliers list, invitations
 * list, and the industries lookup used by the invite/detail flows. The
 * parent page passes the returned refresh callables into modals that mutate
 * the underlying rows (create, resend, bulk upload).
 */
export default function useSupplierData() {
  const [supplierList, setSupplierList] = useState(null);
  const [invitationList, setInvitationList] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [industriesLoading, setIndustriesLoading] = useState(false);

  const getSuppliers = useCallback(async () => {
    setSuppliersLoading(true);
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}suppliers`,
      {},
      {},
      "GET"
    );
    setSuppliersLoading(false);
    if (isSuccess) {
      const rows = (data?.data ?? []).map(normaliseRow);
      setSupplierList(rows.reverse());
    }
  }, []);

  const getInvitations = useCallback(async () => {
    setInvitationsLoading(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}suppliers/invitation`,
        {},
        {},
        "GET",
        true,
        false
      );
      const rows = isSuccess
        ? extractInvitationList(data).map(normaliseInvitationRow)
        : [];
      setInvitationList(rows);
    } finally {
      setInvitationsLoading(false);
    }
  }, []);

  const getIndustries = useCallback(async () => {
    setIndustriesLoading(true);
    try {
      const rows = await fetchSupplierIndustries();
      setIndustries(rows);
    } finally {
      setIndustriesLoading(false);
    }
  }, []);

  useEffect(() => {
    getSuppliers();
    getInvitations();
    getIndustries();
  }, [getSuppliers, getInvitations, getIndustries]);

  return {
    supplierList,
    invitationList,
    industries,
    suppliersLoading,
    invitationsLoading,
    industriesLoading,
    getSuppliers,
    getInvitations,
    getIndustries,
  };
}
